// Linking a toolkit (02-DESIGN/08-toolkits/03-linking.md; decision 0014): how
// a person comes to hold a credential a person-scoped toolkit may spend as
// them. The person, in the console, runs the authorization-code flow with
// PKCE against the toolkit's own registered client in their own browser —
// the platform is not in that exchange — and the resulting refresh token is
// stored at the person's own path, sealed toward the tenant's vault, over the
// person's own connection, which the member template admits and nobody
// else's does. A public client exchanges the code itself at the provider's
// token endpoint; a confidential client exchanges through the toolkit's
// reserved `_exchange` verb, because the secret must never reach a browser.
// The path is computed from the scheme and the workload id, never trusted;
// an endpoint that is not https is never followed.
// Unlinking is the delete of every version; revoking at the provider is the
// person's, at the provider's own account page, and the console says so.
import {} from "@nats-io/nats-core";
import { base64UrlEncode, utf8Decode, utf8Encode } from "./encoding.js";
import { ServiceError } from "./envelope.js";
import * as frame from "./frame.js";
import * as toolkit from "./toolkit.js";
import * as vault from "./vault.js";
/** CHALLENGE_METHOD is the one PKCE method the flow uses. */
export const CHALLENGE_METHOD = "S256";
/** pkce mints a verifier and its challenge. */
export async function pkce() {
    const raw = new Uint8Array(32);
    crypto.getRandomValues(raw);
    const verifier = base64UrlEncode(raw);
    const digest = await crypto.subtle.digest("SHA-256", utf8Encode(verifier).slice());
    return { verifier, challenge: base64UrlEncode(new Uint8Array(digest)) };
}
/** newState mints the `state` the authorization request carries and the callback checks: 16 random bytes as base64url. */
export function newState() {
    const raw = new Uint8Array(16);
    crypto.getRandomValues(raw);
    return base64UrlEncode(raw);
}
/** LinkError is thrown for a toolkit that cannot be linked by this flow. */
export class LinkError extends Error {
    constructor(message) {
        super(`linking: ${message}`);
        this.name = "LinkError";
    }
}
/** linkOf is the toolkit's link, or a LinkError naming why the toolkit is not linkable by this flow. */
export function linkOf(tk) {
    if (tk.scope !== toolkit.Scope.person || tk.scheme !== toolkit.Scheme.oauthRefresh) {
        throw new LinkError(`toolkit ${tk.name} is not a person-scoped oauth-refresh toolkit`);
    }
    if (!tk.link ||
        tk.link.authorizeEndpoint === "" ||
        tk.link.tokenEndpoint === "" ||
        tk.link.clientId === "") {
        throw new LinkError(`toolkit ${tk.name} advertises no complete link`);
    }
    if (!isEndpoint(tk.link.authorizeEndpoint) || !isEndpoint(tk.link.tokenEndpoint)) {
        throw new LinkError(`toolkit ${tk.name} advertises an endpoint that is not https`);
    }
    return tk.link;
}
const LOOPBACK = new Set(["localhost", "127.0.0.1", "[::1]"]);
/**
 * isEndpoint reports whether an advertised endpoint is one the flow may send
 * the person or the code to: an `https` URL with a host, or `http` on a
 * loopback host for development. Anything else — `javascript:`, `data:`, an
 * `http` provider on the network — is a toolkit the console does not follow.
 */
export function isEndpoint(raw) {
    let u;
    try {
        u = new URL(raw);
    }
    catch {
        return false;
    }
    if (u.hostname === "") {
        return false;
    }
    return u.protocol === "https:" || (u.protocol === "http:" && LOOPBACK.has(u.hostname));
}
/**
 * authorizationUrl composes the authorization request against the toolkit's
 * own registered client: the code flow with PKCE, the scopes the link names,
 * the console's redirect and the state.
 */
export function authorizationUrl(tk, a) {
    const link = linkOf(tk);
    const u = new URL(link.authorizeEndpoint);
    u.searchParams.set("response_type", "code");
    u.searchParams.set("client_id", link.clientId);
    u.searchParams.set("redirect_uri", a.redirectUri);
    if (link.scopes.length > 0) {
        u.searchParams.set("scope", link.scopes.join(" "));
    }
    u.searchParams.set("state", a.state);
    u.searchParams.set("code_challenge", a.challenge);
    u.searchParams.set("code_challenge_method", CHALLENGE_METHOD);
    return u.toString();
}
/** MAX_TOKEN_REPLY bounds what the flow reads of a token endpoint's answer. */
export const MAX_TOKEN_REPLY = 64 * 1024;
/**
 * postToken posts a grant to a token endpoint and reads its answer, deciding
 * the contract's code from the HTTP layer as the Go library does: a 4xx is
 * `upstream-auth-failed` — the provider refused the grant and the link is
 * made again — and a dial failure, a 5xx or an unreadable answer is
 * `upstream-unavailable`. The provider's words stay out of the error.
 */
export async function postToken(endpoint, form, opts = {}) {
    const f = opts.fetch ?? fetch;
    let resp;
    try {
        resp = await f(endpoint, {
            method: "POST",
            headers: { "content-type": "application/x-www-form-urlencoded", accept: "application/json" },
            body: new URLSearchParams(form).toString(),
            signal: opts.signal ?? null,
        });
    }
    catch (err) {
        throw new ServiceError(toolkit.Code.upstreamUnavailable, "the token endpoint is unreachable", {
            cause: err,
        });
    }
    let body;
    try {
        const bytes = new Uint8Array(await resp.arrayBuffer());
        if (bytes.length > MAX_TOKEN_REPLY) {
            throw new Error("over the bound");
        }
        body = utf8Decode(bytes);
    }
    catch (err) {
        throw new ServiceError(toolkit.Code.upstreamUnavailable, "the token endpoint's answer could not be read", {
            cause: err,
        });
    }
    if (resp.status >= 500) {
        throw new ServiceError(toolkit.Code.upstreamUnavailable, `the token endpoint answered ${resp.status}`);
    }
    if (resp.status >= 400) {
        throw new ServiceError(toolkit.Code.upstreamAuthFailed, `the provider refused the grant (${resp.status})`);
    }
    if (resp.status < 200 || resp.status >= 300) {
        throw new ServiceError(toolkit.Code.upstreamUnavailable, `the token endpoint answered ${resp.status}`);
    }
    try {
        const parsed = JSON.parse(body);
        if (typeof parsed !== "object" || parsed === null) {
            throw new Error("not an object");
        }
        return parsed;
    }
    catch (err) {
        throw new ServiceError(toolkit.Code.upstreamUnavailable, "the token endpoint's answer is not JSON", {
            cause: err,
        });
    }
}
/**
 * exchangePublic exchanges the code at the provider's token endpoint with
 * the verifier, holding only the client id — the public client's exchange,
 * run by the console itself. The refresh token is what the deposit stores;
 * any access token is discarded, because the runtime mints its own per call.
 */
export async function exchangePublic(tk, x, opts = {}) {
    const link = linkOf(tk);
    if (link.clientType !== toolkit.ClientType.public) {
        throw new LinkError(`toolkit ${tk.name} is not a public client; exchange through the toolkit`);
    }
    const reply = await postToken(link.tokenEndpoint, {
        grant_type: "authorization_code",
        code: x.code,
        code_verifier: x.verifier,
        client_id: link.clientId,
        redirect_uri: x.redirectUri,
    }, opts);
    if (reply.refresh_token === undefined || reply.refresh_token === "") {
        throw new ServiceError(toolkit.Code.upstreamAuthFailed, `the provider issued no refresh token for toolkit ${tk.name}`);
    }
    return reply.refresh_token;
}
/**
 * exchangeConfidential exchanges the code through the toolkit's reserved
 * `_exchange` verb — the toolkit reads its secret at its own prefix and
 * answers the refresh token sealed toward the caller, depositing nothing.
 * The request is sealed toward the key the toolkit advertised at discovery,
 * because every toolkit is the service `toolkit`, and bears the carrier the
 * verb requires.
 */
export async function exchangeConfidential(nc, caller, tk, x, opts = {}) {
    const link = linkOf(tk);
    if (link.clientType !== toolkit.ClientType.confidential) {
        throw new LinkError(`toolkit ${tk.name} is a public client; exchange at the provider`);
    }
    if (tk.workload === "" || tk.sealingKey === "") {
        throw new LinkError(`toolkit ${tk.name} advertises no workload or no sealing key`);
    }
    const body = { code: x.code, verifier: x.verifier, redirect_uri: x.redirectUri };
    const r = await frame.open(nc, caller, {
        service: toolkit.SERVICE,
        sealingKey: tk.sealingKey,
        subject: toolkit.exchangeSubject(tk.workload),
        body: utf8Encode(JSON.stringify(body)),
        headers: opts.headers,
        idle: opts.idle,
        signal: opts.signal,
    });
    const got = await frame.collect(r);
    let reply;
    try {
        reply = JSON.parse(utf8Decode(got.data));
    }
    catch (err) {
        throw new Error(`linking: the exchange reply of ${tk.name} is not JSON`, { cause: err });
    }
    if (typeof reply.refresh_token !== "string" || reply.refresh_token === "") {
        throw new ServiceError(toolkit.Code.upstreamAuthFailed, `the provider issued no refresh token for toolkit ${tk.name}`);
    }
    return reply.refresh_token;
}
/**
 * credentialPath is the person's own path for the toolkit,
 * `person.<principal>.toolkit.<id>.<scheme>`, computed from the scheme the
 * console read and the toolkit's workload id and from nothing else a toolkit
 * advertises. A toolkit with no scheme or no workload cannot be linked.
 */
export function credentialPath(principal, tk) {
    if (tk.workload === "" || tk.scheme === "" || tk.scheme === toolkit.Scheme.none) {
        throw new LinkError(`toolkit ${tk.name} has no credential path`);
    }
    return toolkit.personCredentialPath(principal, tk.workload, tk.scheme);
}
/**
 * deposit stores the credential at the person's own path through the vault's
 * store on the person's own connection: the refresh token of an oauth-refresh
 * toolkit, or the record a token toolkit's setup schema describes, as JSON.
 */
export function deposit(secrets, principal, tk, credential, opts = {}) {
    const value = typeof credential === "string" ? credential : JSON.stringify(credential);
    return secrets.store(credentialPath(principal, tk), value, undefined, opts);
}
/**
 * unlink deletes every version at the person's path, so the platform stops
 * presenting a credential it should no longer hold and the toolkit reads not
 * linked again. It does not revoke access at the provider; the console says
 * so and points the person at the provider's own account page.
 */
export function unlink(secrets, principal, tk, opts = {}) {
    return secrets.delete(credentialPath(principal, tk), true, opts);
}
/**
 * linkedPaths lists the person's own toolkit paths — `vault.list` under
 * `person.<principal>.toolkit`, which answers whether a value exists without
 * revealing it — so the Toolkits page reads linked or configured for each.
 */
export async function linkedPaths(secrets, principal, opts = {}) {
    const reply = await secrets.list(vault.personPath(principal, toolkit.PERSON_TOOLKIT_SEGMENT), opts);
    return new Set(reply.paths.map((e) => e.path));
}
/** isLinked reports whether the person's path for the toolkit is among the paths listed. */
export function isLinked(paths, principal, tk) {
    try {
        return paths.has(credentialPath(principal, tk));
    }
    catch {
        return false;
    }
}
//# sourceMappingURL=linking.js.map